<template>
  <TableCard
    ref="datatable"
    v-model:sort="sorting"
    v-model:page="page"
    v-model:page-size="pageSize"
    class="ui-flex-grow"
    :header="$t('paymentHistory.paymentHistory')"
    :loading="loading"
    :columns="columns"
    :sort-by-name="sortByName"
    :sort-dir-name="sortDirName"
    :rows="payments"
    :currency="currencyLabel"
    :show-total="false"
    :count="paymentsCount"
    :formaters="formaters"
    dont-fill-empty
    extend-height
    i18n-path="reports"
    @fetch-data="fetchDataTable"
  />
</template>

<script>
import Formaters from '@/views/Dashboard/CustomReports/formaters';
import TableCard from '@/components/Cards/TableCard.vue';
import UrlMixin from '@/components/Query/url-mixin';
import InjectPlugins from '@/components/Report/Components/inject-plugins';
import HelpersMixin from '@/components/Helpers/helpers-mixin';
import { pageSizeMixin, resolvePageSize } from '@/views/Dashboard/Reports/page_size';
import tableFlowMixinGenerator from '@/views/Dashboard/Reports/tableFlowMixin';
import formatCurrency from '@/mixins/format-currency';

const sizes = {
  1080: 15,
  1440: 25,
};
const viewName = '/wallet/payment_history';
const pageLimitProp = 'pageSize';
const pageSize = resolvePageSize(viewName, {
  _default: 10,
  sizes,
});

const defaultParams = {
  page: 1,
  pageSize,
  sort_column_history: 'incrementing_id',
  sort_dir_history: 'desc',
};

const statusClasses = {
  approved: 'text-green',
  on_review: 'text-orange',
  declined: 'text-red',
  paid: 'text-green',
  processing: 'text-orange',
  not_paid: 'text-red',
  for_rework: 'text-red',
  failed: 'text-red',
  cancelled: 'text-red',
};

const fetch = async (ctx, params = {}) => {
  if (
    (params.sort_column_history === 'index' && params.sort_dir_history === 'asc')
    || !(params.sort_column_history && params.sort_dir_history)
  ) {
    params.sort_column_history = 'incrementing_id';
    params.sort_dir_history = 'desc';
  }

  const {
    sort_column_history: sortColumn,
    sort_dir_history: sortDir,
    ...restParams
  } = params;

  const data = await ctx.$api.getPaymentsHistory({
    limit: ctx.pageSize || pageSize,
    offset: 0,
    sort_column: sortColumn,
    sort_dir: sortDir,
    ...restParams,
  });

  ctx.payments = data?.payload || [];
  ctx.paymentsCount = data?.misc?.count || 0;
};

const tableFlowMixin = tableFlowMixinGenerator({
  defaultParams: {
    ...defaultParams,
  },
  params: Object.keys(defaultParams),
  fetch,
  windowRefreshHook: true,
  sortByName: 'sort_column_history',
  sortDirName: 'sort_dir_history',
});

export default {
  name: 'PaymentsHistoryTable',
  fetch,
  components: {
    TableCard,
  },
  mixins: [
    UrlMixin,
    InjectPlugins,
    HelpersMixin,
    pageSizeMixin,
    tableFlowMixin,
    formatCurrency,
  ],
  props: ['passedParams'],
  data() {
    return {
      viewName,
      pageLimitProp,
      loading: false,
      payments: [],
      paymentsCount: 0,
      pagination: true,
      formaters: Formaters,
      isTableReady: false,
    };
  },
  beforeMount() {
    this.pageSize = this.limit;
  },
  computed: {
    currencyLabel() {
      return this.getCurrencyLabel(this.$store.state.payments.currencyCode);
    },
    columns() {
      return [
        {
          name: 'incrementing_id',
          sortable: 'custom',
          filterable: false,
          width: 54,
        },
        {
          name: 'type',
          title: this.$t('paymentHistory.type'),
          sortable: false,
          filterable: false,
          width: 90,
          renderValue: (value) => this.formatType(value),
        },
        {
          name: 'moderation_status',
          title: this.$t('mediaBuying.payments.status.moderation_status'),
          sortable: false,
          filterable: false,
          width: 120,
          renderValue: (value) => this.formatStatus(value),
        },
        {
          name: 'payout_status',
          title: this.$t('mediaBuying.payments.status.payout_status'),
          sortable: false,
          filterable: false,
          width: 120,
          renderValue: (value) => this.formatStatus(value),
        },
        {
          name: 'amount',
          title: this.$t('paymentHistory.amount'),
          sortable: false,
          filterable: false,
          align: 'right',
          width: 84,
          renderValue: (val, row) => this.currencyFormatter(val, row.currency_code),
        },
        {
          name: 'method',
          sortable: false,
          filterable: false,
          renderValue: (_, row) => {
            const name = row.site_payment_method_name;
            const logo = row.site_payment_method_logo_base64;

            if (!name && !logo) {
              return '';
            }

            return `<span class="payment">${logo ? `<img height="18" src="${logo}" alt="${name}" />` : name}</span>`;
          },
        },
        {
          name: 'wallet',
          sortable: false,
          filterable: false,
          width: 120,
          renderValue: (_, row) => row.payment_wallet,
        },
        {
          name: 'created_at',
          title: this.$t('paymentHistory.createdAt'),
          sortable: 'custom',
          filterable: false,
          format: 'date-time-sec',
          width: 146,
        },
        {
          name: 'paid_at',
          title: this.$t('paymentHistory.paidAt'),
          sortable: 'custom',
          filterable: false,
          format: 'date-time-sec',
          width: 146,
        },
      ];
    },
  },
  created() {
    this.setFromQuery(this.passedParams?.paymentHistory);
  },
  async mounted() {
    this.fetchData(this.currentQuery);
  },
  methods: {
    cacheQuery() {
      this.pushQuery(this.$route, {
        balanceHistory: this.urlQuery.balanceHistory,
        paymentHistory: this.cacheData,
      });
    },
    formatStatus(value) {
      if (!value) {
        return '';
      }

      const key = `mediaBuying.payments.status.${value}`;
      const label = this.$te(key) ? this.$t(key) : value;
      const color = statusClasses[value];

      return color ? `<span class="${color}">${label}</span>` : label;
    },
    formatType(value) {
      if (!value) {
        return '';
      }

      const key = `paymentHistory.${value}`;

      return this.$te(key) ? this.$t(key) : value;
    },
    currencyFormatter(amount, code) {
      const sum = this.$filters.currency(amount, this.getCurrencyLabel(code), null, {
        symbolOnLeft: true,
        spaceBetweenAmountAndSymbol: false,
        decimalSeparator: ',',
      });

      if (Math.abs(amount) <= 0.005) {
        return `<span>${sum.replace('-', '')}</span>`;
      }

      return `<span>${sum}</span>`;
    },
  },
};
</script>
